// modules/rive-player.js
import { onMount, onDestroy } from "@/modules/_";
import { Rive, Fit, Alignment, Layout } from "@rive-app/webgl2";

/**
 * Usage in HTML/Webflow:
 * <div data-module="rive"
 *      data-rive="ani-2"
 *      data-rive-src="https://cdn.example.com/your.riv"
 *      data-rive-fit="contain"            <!-- cover | contain | fill | fit-width | fit-height | none -->
 *      data-rive-alignment="bottom-center"<!-- top-left ... bottom-right -->
 *      data-rive-autoplay="true"
 *      data-rive-play-once="false"        <!-- if true, animation plays only once when entering viewport -->
 *      data-rive-threshold="0.2"          <!-- % of element visible before triggering (0-1) -->
 *      data-rive-sm="State Machine 1">
 *   <canvas class="cc-rive-2" style="width:100%;height:100%;display:block;"></canvas>
 * </div>
 */
export default function rivePlayer(element, dataset) {
  const canvas =
    element.tagName === "CANVAS" ? element : element.querySelector("canvas");
  if (!canvas) {
    console.warn("[rive] No <canvas> found");
    return;
  }

  // Config from data-attributes with sane defaults
  const artboard      = dataset.rive || "ani-1";
  const riveSrc       = dataset.riveSrc || "https://cdn.prod.website-files.com/686294e263eb7e215bd232f7/68f10995c151c9f81b2ef353_fff6f54180720a23efe54c719128b791_webflow_sotw.riv";
  const stateMachine  = dataset.riveSm || dataset.riveStateMachine || "State Machine 1";
  const autoplay      = dataset.riveAutoplay !== "false";
  const playOnce      = dataset.rivePlayOnce === "true";
  const threshold     = parseFloat(dataset.riveThreshold) || 0.2;

  const alignmentMap = {
    "top-left": Alignment.TopLeft,
    "top-center": Alignment.TopCenter,
    "top-right": Alignment.TopRight,
    "center-left": Alignment.CenterLeft,
    "center": Alignment.Center,
    "center-right": Alignment.CenterRight,
    "bottom-left": Alignment.BottomLeft,
    "bottom-center": Alignment.BottomCenter,
    "bottom-right": Alignment.BottomRight,
  };
  const fitMap = {
    cover: Fit.Cover,
    contain: Fit.Contain,
    fill: Fit.Fill,
    "fit-width": Fit.FitWidth,
    "fit-height": Fit.FitHeight,
    none: Fit.None,
  };

  const alignment = alignmentMap[(dataset.riveAlignment || "").toLowerCase()] || Alignment.BottomCenter;
  const fitMode   = fitMap[(dataset.riveFit || "").toLowerCase()] || Fit.Contain;

  // Ensure CSS sizing controls layout; bitmap size will be synced in JS.
  canvas.style.width = canvas.style.width || "100%";
  canvas.style.height = canvas.style.height || "100%";
  canvas.style.display = "block";

  // IMPORTANT: remove hard attributes from exported HTML so bitmap isn’t frozen
  canvas.removeAttribute("width");
  canvas.removeAttribute("height");

  let riveInstance = null;
  let rafId = null;
  let resizeTimeout = null;
  let observer = null;
  let hasPlayedOnce = false;
  let isInView = false;

  const resizeToCSS = () => {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
  };

  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      resizeToCSS();
      riveInstance?.resizeDrawingSurfaceToCanvas();
    }, 80);
  };

  const handleVisibilityChange = () => {
    if (!riveInstance) return;
    if (document.hidden) {
      riveInstance.pause();
    } else if (autoplay && isInView) {
      riveInstance.play();
    }
  };

  const setupIntersectionObserver = () => {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isInView = entry.isIntersecting;

          if (entry.isIntersecting) {
            // Element entered viewport
            if (!riveInstance) {
              // First time in view - initialize
              initRive();
            } else if (autoplay && !hasPlayedOnce) {
              // Already initialized but should play
              riveInstance.play();
              const inputs = riveInstance.stateMachineInputs(stateMachine);
              const startTrigger = inputs?.find((i) => i.name === "start");
              startTrigger?.fire();
            }

            if (playOnce) {
              hasPlayedOnce = true;
            }
          } else {
            // Element left viewport
            if (riveInstance && autoplay && !playOnce) {
              // Pause when out of view (unless it's play-once mode)
              riveInstance.pause();
            }
          }
        });
      },
      {
        threshold,
        rootMargin: "0px",
      }
    );

    observer.observe(element);
  };

  const initRive = () => {
    try {
      riveInstance = new Rive({
        src: riveSrc,
        canvas,
        artboard,
        stateMachines: stateMachine,
        autoplay,
        // Required: Fit + Alignment must be provided via Layout
        layout: new Layout({
          fit: fitMode,
          alignment,
        }),
        onLoad: () => {
          resizeToCSS();
          riveInstance.resizeDrawingSurfaceToCanvas();

          // Fire "start" trigger if present and autoplaying
          const inputs = riveInstance.stateMachineInputs(stateMachine);
          const startTrigger = inputs?.find((i) => i.name === "start");
          if (startTrigger && autoplay) startTrigger.fire();
        },
        onLoadError: (err) => {
          console.error("[rive] Load error:", err);
        },
      });
    } catch (err) {
      console.error("[rive] Init error:", err);
    }
  };

  // Public controls
  const play = () => {
    if (!riveInstance) return;
    riveInstance.play();
    const inputs = riveInstance.stateMachineInputs(stateMachine);
    const startTrigger = inputs?.find((i) => i.name === "start");
    startTrigger?.fire();
  };
  const pause = () => riveInstance?.pause();
  const reset = () => riveInstance?.reset();
  const trigger = (name) => {
    if (!riveInstance) return;
    const inputs = riveInstance.stateMachineInputs(stateMachine);
    const t = inputs?.find((i) => i.name === name);
    t?.fire();
  };

  element.riveControls = {
    play,
    pause,
    reset,
    trigger,
    getInstance: () => riveInstance,
  };

  onMount(() => {
    setupIntersectionObserver();
    window.addEventListener("resize", handleResize, { passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);
  });

  onDestroy(() => {
    clearTimeout(resizeTimeout);
    window.removeEventListener("resize", handleResize);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (rafId) cancelAnimationFrame(rafId);
    riveInstance?.cleanup();
    riveInstance = null;
    delete element.riveControls;
  });
}
